// GEOBAJA — Persistencia de la sesión (token + refresh + perfil).
//
// Problema: en sessionStorage la sesión se borra cada vez que iOS/Android
// suspenden o cierran la app instalada (PWA en la pantalla de inicio), forzando
// a iniciar sesión "cada rato". localStorage SÍ sobrevive a esos cierres.
//
// Criterio (seguro por defecto):
//  - App INSTALADA (standalone, agregada al inicio): localStorage → la sesión
//    persiste entre aperturas; ya no hay que volver a entrar todo el tiempo.
//  - Navegador normal (pestaña): sessionStorage → efímera (más segura en
//    equipos compartidos/públicos), igual que antes.
// Se incluye un fallback que migra una sesión ya creada del otro almacén, para
// que este cambio no desloguee a quien ya estaba dentro.
//
// Todo va envuelto en try/catch: en modo privado o con almacenamiento bloqueado
// las funciones degradan a no-op en vez de tumbar la app.

const KEYS = ['sb_token', 'sb_refresh_token', 'sb_perfil'];

// ¿La app corre como PWA instalada (standalone / fullscreen / minimal-ui)?
// `navigator.standalone` es el indicador de iOS Safari; display-mode cubre el
// resto (Android/desktop). Estable durante toda la sesión de la ventana.
export function esAppInstalada() {
  try {
    if (typeof window === 'undefined') return false;
    if (window.navigator && window.navigator.standalone === true) return true;
    const mm = window.matchMedia;
    return !!mm && (
      mm('(display-mode: standalone)').matches ||
      mm('(display-mode: fullscreen)').matches ||
      mm('(display-mode: minimal-ui)').matches
    );
  } catch (e) { return false; }
}

function primario() {
  try { return esAppInstalada() ? window.localStorage : window.sessionStorage; }
  catch (e) { return null; }
}

function secundario() {
  // El "otro" almacén: se usa solo para leer/migrar una sesión previa.
  try { return esAppInstalada() ? window.sessionStorage : window.localStorage; }
  catch (e) { return null; }
}

export function getAuthItem(key) {
  try {
    const p = primario();
    const v = p ? p.getItem(key) : null;
    if (v != null) return v;
    // Migración SOLO hacia la app instalada: adoptar una sesión previa guardada
    // en sessionStorage del MISMO contexto (PWA), antes de este cambio. Nunca al
    // revés: una pestaña del navegador NO debe heredar la sesión persistente de
    // la PWA (seguridad en equipos compartidos). Sin instalación → sessionStorage
    // exclusivamente.
    if (!esAppInstalada()) return null;
    const s = secundario();   // sessionStorage cuando la app está instalada
    const legacy = s ? s.getItem(key) : null;
    if (legacy != null && p) { try { p.setItem(key, legacy); } catch (e) { /* sin persistencia */ } }
    return legacy;
  } catch (e) { return null; }
}

export function setAuthItem(key, value) {
  try { const p = primario(); if (p) p.setItem(key, value); } catch (e) { /* sin persistencia */ }
}

export function removeAuthItem(key) {
  // Borra en AMBOS almacenes: logout/expiración no deben dejar rastro.
  try { window.localStorage.removeItem(key); } catch (e) { /* no-op */ }
  try { window.sessionStorage.removeItem(key); } catch (e) { /* no-op */ }
}

// Salir o perder la sesión borra la sesión guardada de ESTE contexto (2026-09-16). La app
// instalada borra su localStorage y la sesión legada de su sessionStorage; una pestaña del
// navegador solo su sessionStorage. Antes borraba los dos almacenes desde cualquier lado, y
// el localStorage es de la app instalada: salir en una pestaña de Chrome dejaba sin sesión a
// la app del técnico en la misma tablet (con otra persona o la misma), y sin señal ya no
// podía volver a entrar. Una pestaña nunca lee el localStorage (getAuthItem), así que ahí
// no hay rastro suyo que borrar.
export function clearAuth() {
  const instalada = esAppInstalada();
  KEYS.forEach((key) => {
    if (instalada) removeAuthItem(key);
    else {
      try { window.sessionStorage.removeItem(key); } catch (e) { /* no-op */ }
    }
  });
}
